/**
 * Top-level codes for Observations
 */
export enum ObservationCode {
  clinicalImpression = 'observation.clinicalimpression',
  vitals = 'observation.vitals',
  calibration = 'observation.calibration',
  pregnancy = 'observation.pregnancy',
  lactation = 'observation.lactation',
  edd = 'observation.expecteddeliverydate',
  pathologyTestResult = 'observation.pathology.test.result',

  // deprecated reading types from when vitals were returned as observations
  /** @deprecated */
  patchPosture = 'observation.patch.posture',
  /** @deprecated */
  patchHeartRate = 'observation.patch.heartrate',
  /** @deprecated */
  patchRespiratoryRate = 'observation.patch.respatoryrate',
  /** @deprecated */
  patchSteps = 'observation.patch.steps',
  /** @deprecated */
  patchCoreTemperature = 'observation.patch.coretemperature',
  /** @deprecated */
  patchBattery = 'observation.patch.battery',
  /** @deprecated */
  mightySatOxygenSaturation = 'observation.relay.oxygensaturation',
}

export const ObservationCodeDescription: Record<ObservationCode, string> = {
  [ObservationCode.patchPosture]: 'Posture',
  [ObservationCode.patchHeartRate]: 'Heart Rate',
  [ObservationCode.patchRespiratoryRate]: 'Respiratory Rate',
  [ObservationCode.patchSteps]: 'Steps',
  [ObservationCode.patchCoreTemperature]: 'Core Temperature',
  [ObservationCode.patchBattery]: 'Patch Battery',
  [ObservationCode.mightySatOxygenSaturation]: 'Oxygen Saturation',
  [ObservationCode.clinicalImpression]: 'Note',
  [ObservationCode.vitals]: 'Vitals',
  [ObservationCode.calibration]: 'Calibration',
  [ObservationCode.pregnancy]: 'Pregnancy',
  [ObservationCode.lactation]: 'Lactation',
  [ObservationCode.edd]: 'Expected Delivery Date',
  [ObservationCode.pathologyTestResult]: 'Pathology Test Result',
};

export enum ObservationMeasurementCode {
  Activity = 'observation.measurement.activity',
  HeartRate = 'observation.measurement.heartrate',
  RespiratoryRate = 'observation.measurement.respiratoryrate',
  SkinTemperature = 'observation.measurement.skintemperature',
  CoreTemperature = 'observation.measurement.coretemperature',
  OxygenSaturation = 'observation.measurement.oxygensaturation',
  PerfusionIndex = 'observation.measurement.perfusionindex',
  Steps = 'observation.measurement.steps',
  Posture = 'observation.measurement.posture',
  Battery = 'observation.measurement.battery',
  RRI = 'observation.measurement.rri',
  ECG = 'observation.measurement.ecg',
  Glucose = 'observation.measurement.glucose',
  GlucoseBeforeMeal = 'observation.measurement.glucosebeforemeal',
  GlucoseAfterMeal = 'observation.measurement.glucoseaftermeal',
  GlucoseFasting = 'observation.measurement.glucosefasting',
  SystolicArterialBloodPressure = 'observation.measurement.systolicarterialbloodpressure',
  DiastolicArterialBloodPressure = 'observation.measurement.diastolicarterialbloodpressure',
  MeanArterialBloodPressure = 'observation.measurement.meanarterialbloodpressure',
  /**
   * Blood pressure represented in the `SYS/DIA` format
   */
  CombinedArterialBloodPressure = 'observation.measurement.combinedarterialbloodpressure',
  Weight = 'observation.measurement.weight',
  PostureCalibration = 'observation.measurement.posturecalibration',
  EarlyWarningScore = 'observation.measurement.earlywarningscore',
  ButtonPress = 'observation.measurement.buttonpress',
  SensorLowQuality = 'observation.measurement.sensorlowquality',
  Fall = 'observation.measurement.fall',
  WearStatus = 'observation.measurement.wearstatus',
  DeviceStatus = 'observation.measurement.devicestatus',
}

export const ObservationMeasurementUnits: Record<
  ObservationMeasurementCode,
  string | undefined
> = {
  [ObservationMeasurementCode.Activity]: undefined,
  [ObservationMeasurementCode.HeartRate]: 'bpm',
  [ObservationMeasurementCode.RespiratoryRate]: 'bpm',
  [ObservationMeasurementCode.SkinTemperature]: '°C',
  [ObservationMeasurementCode.CoreTemperature]: '°C',
  [ObservationMeasurementCode.OxygenSaturation]: '%',
  [ObservationMeasurementCode.PerfusionIndex]: undefined,
  [ObservationMeasurementCode.Steps]: undefined,
  [ObservationMeasurementCode.Posture]: undefined,
  [ObservationMeasurementCode.Battery]: '%',
  [ObservationMeasurementCode.RRI]: undefined,
  [ObservationMeasurementCode.ECG]: undefined,
  [ObservationMeasurementCode.Glucose]: 'mmol/l',
  [ObservationMeasurementCode.GlucoseBeforeMeal]: 'mmol/l',
  [ObservationMeasurementCode.GlucoseAfterMeal]: 'mmol/l',
  [ObservationMeasurementCode.GlucoseFasting]: 'mmol/l',
  [ObservationMeasurementCode.SystolicArterialBloodPressure]: 'mm Hg',
  [ObservationMeasurementCode.DiastolicArterialBloodPressure]: 'mm Hg',
  [ObservationMeasurementCode.MeanArterialBloodPressure]: 'mm Hg',
  [ObservationMeasurementCode.CombinedArterialBloodPressure]: 'mm Hg',
  [ObservationMeasurementCode.Weight]: 'kg',
  [ObservationMeasurementCode.PostureCalibration]: undefined,
  [ObservationMeasurementCode.EarlyWarningScore]: undefined,
  [ObservationMeasurementCode.ButtonPress]: undefined,
  [ObservationMeasurementCode.SensorLowQuality]: undefined,
  [ObservationMeasurementCode.Fall]: undefined,
  [ObservationMeasurementCode.WearStatus]: undefined,
  [ObservationMeasurementCode.DeviceStatus]: undefined,
};
